import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { RUN_KINDS, isRunKind } from "../../../shared/goals/runs";
import { BackLink } from "../../components/ui/BackLink";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Info } from "../../components/ui/Info";
import { ccApi, type RunLine } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { runsApi, type RunEvent } from "../../lib/productsApi";
import { buildLog, type LogEntry } from "../goal/runEvents";
import { dayLabel, hm, parseTime, reviewWhen, sameDay } from "./text";
import { errText, useHome } from "./useCc";
import "../../styles/wizard.css";
import "../../styles/cc.css";

export type Kind = "run" | "note" | "think" | "call" | "guard" | "err";
export interface Row extends Omit<LogEntry, "k"> {
  k: Kind;
}

export const ICON: Record<Kind, IconName> = { run: "play", note: "toggle", think: "bulb", call: "tool", guard: "shield", err: "x" };
export const FILTERS: [string, string][] = [
  ["all", "All"],
  ["think", "Thinking"],
  ["call", "Steps"],
  ["guard", "Rules"],
];
export const PAGE = 30;
export const runName = (r: RunLine) =>
  r.kind === "review" && sameDay(parseTime(r.startedAt ?? r.createdAt), new Date()) ? "This morning's review" : isRunKind(r.kind) ? RUN_KINDS[r.kind].label : r.kind;
export const runWhen = (r: RunLine) => {
  const s = parseTime(r.startedAt ?? r.createdAt);
  return `${dayLabel(s)} ${hm(s)}${r.endedAt ? ` to ${hm(parseTime(r.endedAt))}` : ""}`;
};

const t = (d: Date) => `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;

/** A run's log rows: when it started, every step (calls that ask Flashadz to act count as rule checks) and its end. */
function rowsOf(run: RunLine, events: RunEvent[], next: string | null): Row[] {
  const log: Row[] = buildLog({ started_at: run.startedAt, created_at: run.createdAt }, events).map((e) => ({ ...e, t: e.at ? t(parseTime(e.at)) : e.t, k: e.k === "call" && (e.tool ?? "").startsWith("propose_") ? "guard" : e.k }));
  const start = parseTime(run.startedAt ?? run.createdAt);
  const end = run.endedAt ? parseTime(run.endedAt) : null;
  const rows: Row[] = [{ key: "start", at: "", t: t(start), k: "run", x: `Task started${run.trigger ? ` by ${run.trigger === "schedule" ? "the schedule" : run.trigger === "chat" ? "your message" : "you"}` : ""}: ${(isRunKind(run.kind) ? RUN_KINDS[run.kind].label : run.kind).toLowerCase()}` }, ...log];
  if (end) {
    rows.push({ key: "end", at: "", t: t(end), k: "run", x: run.state === "done" ? `Run finished${next ? `. Next review ${next}` : ""}` : `Run ${run.state}${run.error ? `: ${run.error}` : ""}` });
  }
  return rows;
}

function LogRow({ e }: { e: Row }) {
  const [open, setOpen] = useState(false);
  const det = e.i !== undefined || e.o !== undefined;
  const raw = [
    e.tool ? `tool\n${e.tool}\n\n` : "",
    e.i !== undefined ? `input\n${JSON.stringify(e.i, null, 2)}\n\n` : "",
    e.o !== undefined ? `output\n${typeof e.o === "string" ? e.o : JSON.stringify(e.o, null, 2)}` : "",
  ].join("");
  return (
    <div className={`lr ${e.k}${det ? " det" : ""}`} onClick={det ? () => setOpen((o) => !o) : undefined}>
      <span className="lt">{e.t}</span>
      <span className="li">
        <Icon name={ICON[e.k]} />
      </span>
      <div className="lx">
        <span className="ltx">{e.x}</span>
        {det && <span className="lmore">{open ? "Hide" : "Details"}</span>}
        {open && <pre className="raw">{raw}</pre>}
      </div>
    </div>
  );
}

/** Every event page of a run, read to its end. */
async function allEvents(runId: string): Promise<RunEvent[]> {
  const out: RunEvent[] = [];
  for (let after = 0; ; ) {
    const { events } = await runsApi.events(runId, after);
    out.push(...events);
    if (events.length < 200) return out;
    after = events[events.length - 1].seq;
  }
}

/** A goal's runs, newest first, the one picked (`?run=`), and its steps filtered and searched: for the log on any screen. */
export function useLog(view: GoalView) {
  const g = view.goal;
  const [params, setParams] = useSearchParams();
  const [runs, setRuns] = useState<RunLine[] | null>(null);
  const [more, setMore] = useState(false);
  const [events, setEvents] = useState<Record<string, RunEvent[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const asked = params.get("run");

  // Why: a link names a run that can be older than the first page; read every run back to it in one call.
  const linked = useRef(asked);
  useEffect(() => {
    let live = true;
    const through = linked.current;
    (async () => {
      try {
        let r = await ccApi.runs(g.id, through ? { through } : { limit: PAGE });
        if (through && !r.runs.length) r = await ccApi.runs(g.id, { limit: PAGE });
        let list = r.runs;
        let full = list.length >= PAGE;
        if (through && list.length && !full) {
          const fill = await ccApi.runs(g.id, { limit: PAGE - list.length, before: list[list.length - 1].createdAt });
          list = [...list, ...fill.runs];
          full = fill.runs.length === PAGE - r.runs.length;
        }
        if (!live) return;
        setRuns(list);
        setMore(full);
      } catch (e) {
        if (live) setError(errText(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [g.id]);

  const older = () => {
    const last = runs?.[runs.length - 1];
    if (!last) return;
    ccApi.runs(g.id, { limit: PAGE, before: last.createdAt }).then(
      (r) => {
        setRuns((x) => [...(x ?? []), ...r.runs.filter((n) => !(x ?? []).some((o) => o.id === n.id))]);
        setMore(r.runs.length === PAGE);
      },
      (e) => setError(errText(e)),
    );
  };

  const run = runs?.find((r) => r.id === asked) ?? runs?.[0] ?? null;
  useEffect(() => {
    if (!run || events[run.id]) return;
    allEvents(run.id).then(
      (ev) => setEvents((m) => ({ ...m, [run.id]: ev })),
      (e) => setError(errText(e)),
    );
  }, [run, events]);

  const { home } = useHome();
  const homeGoal = home?.products.flatMap((p) => p.goals).find((x) => x.id === g.id) ?? null;
  const next = run && run.id === runs?.find((r) => r.kind === "review")?.id ? (reviewWhen(homeGoal?.nextReview ?? null)?.when ?? null) : null;
  const rows = useMemo(() => (run ? rowsOf(run, events[run.id] ?? [], next) : []), [run, events, next]);
  const shown = rows.filter((e) => {
    if (filter === "think" && e.k !== "think") return false;
    if (filter === "call" && !["call", "err", "note"].includes(e.k)) return false;
    if (filter === "guard" && e.k !== "guard") return false;
    const s = q.trim().toLowerCase();
    return !s || `${e.x} ${e.tool ?? ""} ${JSON.stringify(e.i ?? "")} ${JSON.stringify(e.o ?? "")}`.toLowerCase().includes(s);
  });
  const n = (k: Kind) => rows.filter((e) => e.k === k).length;

  const download = () => {
    if (!run) return;
    const blob = new Blob([JSON.stringify({ run, events: events[run.id] ?? [] }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `run-${run.id}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const pick = (id: string) => setParams({ run: id }, { replace: true });

  return { runs, more, older, run, events, error, filter, setFilter, q, setQ, rows, shown, n, download, pick, next };
}

/** The goal's full log (`/goals/:id/log`): every run, newest first, and every step it recorded (CC5). */
export function LogPage({ view }: { view: GoalView }) {
  const g = view.goal;
  const { runs, more, older, run, events, error, filter, setFilter, q, setQ, shown, n, download, pick } = useLog(view);

  return (
    <div className="wrap ga gw ccx cc-gpage">
      <div className="gx-logh">
        <BackLink to={`/goals/${encodeURIComponent(g.id)}`} label="Goal" />
        <h2>Full log</h2>
      </div>
      {!runs ? (
        <p className="load-note">{error ?? "Loading the log…"}</p>
      ) : !run ? (
        <p className="load-note">No tasks for this goal yet.</p>
      ) : (
        <>
          <div className="logtop">
            <div className="runs">
              {runs.map((r) => (
                <button type="button" key={r.id} className={r.id === run.id ? "on" : ""} onClick={() => pick(r.id)}>
                  <b>{runName(r)}</b>
                  <span>{runWhen(r)}</span>
                </button>
              ))}
              {more && (
                <button type="button" onClick={older}>
                  <b>Older runs</b>
                  <span>Show {PAGE} more</span>
                </button>
              )}
            </div>
            <button type="button" className="btn sm ghost" onClick={download}>
              <Icon name="down" />
              Download
            </button>
          </div>
          <div className="logsum mono">
            {n("call") + n("guard")} steps · {n("think")} thoughts · {n("guard")} rule checks
            <Info label="About the log">Every step is written down as it happens. Nobody can edit the log, not even the agent. Tap a row with Details to see what went in and what came back.</Info>
          </div>
          <div className="logctl">
            <div className="lfil">
              {FILTERS.map(([k, l]) => (
                <button type="button" key={k} className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>
                  {l}
                </button>
              ))}
            </div>
            <label className="lsearch">
              <Icon name="search" />
              <input placeholder="Search the log" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the log" />
            </label>
          </div>
          <div className="card log">
            {shown.length ? (
              shown.map((e) => <LogRow key={e.key} e={e} />)
            ) : (
              <div className="lr">
                <span className="lt" />
                <span className="li">
                  <Icon name="search" />
                </span>
                <div className="lx">
                  <span className="ltx">{events[run.id] ? "Nothing matches." : "Loading…"}</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
